import React, { useMemo, useState, useRef } from 'react';
import { Crosshair, ClipboardPlus, Plus, Search, X, WandSparkles, Users, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { Task } from '@/types/tasks';
import TaskCard from '../tasks/TaskCard';
import GlassPill from '@/components/ui/GlassPill';
import ContainerButton from '@/components/ui/ContainerButton';
import CheckCircle2 from '../tasks/components/CheckCircle2';
import DashboardButton from '@/components/ui/DashboardButton';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useNavigate, useParams } from 'react-router-dom';
import { useDebouncedValue } from '@/lib/utils';
import { useTaskFilter } from '@/components/project/ProjectTasksPage';

interface GoalActiveTasksSectionProps {
  tasks: Task[];
  // NEW: Goal-specific task operations
  taskOperations?: {
    completeTask?: (taskId: string) => void;
    uncompleteTask?: (taskId: string) => void;
    removeTask?: (taskId: string) => void;
    updateTask?: (taskId: string, updates: Partial<Task>, silent?: boolean) => void;
  };
  // Function to open page creation modal with task data
  onGenerateContent?: (taskTitle: string, taskDescription: string, taskId: string) => void;
  // Function to open research creation modal with task data
  onGenerateResearch?: (taskTitle: string, taskDescription: string, taskId: string) => void;
  // Function to open image creation modal with task data
  onGenerateImage?: (taskTitle: string, taskDescription: string, taskId: string) => void;
  // Loading state for content generation
  isGeneratingContent?: boolean;
  // Function to open task creation modal
  onCreateTask?: () => void;
  // NEW: Assignee filter props
  selectedAssigneeId?: string;
  onAssigneeChange?: (assigneeId: string) => void;
  teamMembers?: any[];
  isLoadingTeam?: boolean;
  assigneeTaskCounts?: {
    assigneeCounts: Record<string, number>;
    totalTasks: number;
    unassignedCount: number;
  };
  createTaskPanelRef?: React.RefObject<HTMLDivElement>;
  workbookId?: string; // For navigation to generated content
}

const GoalActiveTasksSection: React.FC<GoalActiveTasksSectionProps> = React.memo(({ 
  tasks, 
  taskOperations,
  onGenerateContent,
  onGenerateResearch,
  onGenerateImage,
  isGeneratingContent,
  onCreateTask,
  selectedAssigneeId = 'all',
  onAssigneeChange,
  teamMembers = [],
  isLoadingTeam = false,
  assigneeTaskCounts = { assigneeCounts: {}, totalTasks: 0, unassignedCount: 0 },
  createTaskPanelRef,
  workbookId
}) => {
  // Get filtered tasks from context (includes search + assignee filtering)
  const { searchQuery, filteredActiveTasks } = useTaskFilter();
  
  // Set up droppable area for goal active tasks
  const { setNodeRef } = useDroppable({
    id: 'active-tasks',
  });

  // Use filtered tasks from context if available, otherwise fall back to original behavior
  const finalFilteredTasks = useMemo(() => {
    // If we have filtered tasks from parent context (ProjectTasksPage), use those
    if (filteredActiveTasks && filteredActiveTasks.length >= 0) {
      return filteredActiveTasks;
    }
    
    // Fallback: Apply local search filtering (for other contexts like standalone usage)
    if (!searchQuery) return tasks;
    
    const query = searchQuery.toLowerCase();
    return tasks.filter(task =>
      task.title.toLowerCase().includes(query) ||
      (task.description && task.description.toLowerCase().includes(query))
    );
  }, [filteredActiveTasks, tasks, searchQuery]);

  // Memoize task IDs to prevent SortableContext re-renders
  const taskIds = useMemo(() => finalFilteredTasks.map(task => task.id), [finalFilteredTasks]);

  // Memoize the task count to prevent unnecessary GlassPill re-renders
  const taskCount = useMemo(() => finalFilteredTasks.length, [finalFilteredTasks.length]);

  // Function to open the task creation modal
  const handleCreateTask = () => {
    if (onCreateTask) {
      onCreateTask();
    } else {
      // Fallback to the custom event system if no prop handler provided
      const event = new CustomEvent('openCreateTaskModal');
      window.dispatchEvent(event);
    }
  };

  return (
    <div
      ref={setNodeRef}
      className="relative p-3 md:p-6 rounded-lg overflow-hidden"
    >
      {/* Base Gradient (Bottom Layer) - matching card border radius */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/60 via-white/30 to-white/10 dark:from-black/40 dark:to-black/20 -z-10 rounded-lg" />
      
      {/* Top border highlight - matching card border radius */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-300/30 to-transparent dark:via-amber-500/20 rounded-t-lg" />
      
      {/* Glass effect - matching card border radius */}
      <div className="absolute inset-0 backdrop-blur-xl backdrop-saturate-150 -z-10 rounded-lg" />
      
      {/* Header Section - Mobile responsive layout */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3 md:mb-4">
        {/* Title Section */}
        <div className="flex items-center gap-2">
          <Crosshair className="text-amber-500 dark:text-amber-400" size={18} />
          <h3 className="font-medium text-base">Team Tasks</h3>
          {taskCount > 0 && (
            <GlassPill 
              type={taskCount.toString()} 
              variant="amber" 
              className="ml-2 text-xs h-6 w-6 flex items-center justify-center p-0"
            />
          )}
        </div>
        
        {/* Action Buttons - Stack on mobile, inline on desktop */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          {/* New Task Button */}
          <DashboardButton
            onClick={handleCreateTask}
            icon={ClipboardPlus}
            size="sm"
            className="h-8 px-3 py-1.5 text-sm"
          >
            New Task
          </DashboardButton>
          
          {/* Assignee Filter Dropdown */}
          {onAssigneeChange && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  className="h-8 px-3 bg-white/30 dark:bg-white/10 backdrop-blur-sm border border-accent/20 hover:border-accent/30 justify-between"
                  aria-label="Filter tasks by assignee"
                >
                  <div className="flex items-center gap-2">
                    <Users size={14} className="text-accent" />
                    <span className="text-xs font-medium">
                      {selectedAssigneeId === 'all' 
                        ? `All`
                        : selectedAssigneeId === 'unassigned'
                        ? `Unassigned (${assigneeTaskCounts.unassignedCount})`
                        : (() => {
                            const member = teamMembers.find(member => member.user_id === selectedAssigneeId);
                            const count = assigneeTaskCounts.assigneeCounts[selectedAssigneeId] || 0;
                            return `${member?.display_name || 'Unknown'} (${count})`;
                          })()
                      }
                    </span>
                  </div>
                  <ChevronDown size={12} className="text-accent/60 flex-shrink-0" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="min-w-[200px]">
                <DropdownMenuItem 
                  onClick={() => onAssigneeChange('all')}
                  className="text-slate-700 dark:text-slate-200 focus:text-slate-700 dark:focus:text-slate-200 hover:text-slate-700 dark:hover:text-slate-200 focus:bg-accent/10 hover:bg-accent/10 px-4"
                >
                  <div className="w-6 h-6 rounded-full bg-gradient-to-r from-blue-400 to-purple-500 flex items-center justify-center mr-2">
                    <Users size={12} className="text-white" />
                  </div>
                  <span className="flex-1">All Assignees</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                    ({assigneeTaskCounts.totalTasks})
                  </span>
                </DropdownMenuItem>
                {isLoadingTeam ? (
                  <DropdownMenuItem disabled>
                    <span className="text-sm text-gray-500">Loading team members...</span>
                  </DropdownMenuItem>
                ) : (
                  teamMembers
                    .filter((member) => {
                      const taskCount = assigneeTaskCounts.assigneeCounts[member.user_id] || 0;
                      return taskCount > 0;
                    })
                    .map((member) => (
                      <DropdownMenuItem 
                        key={member.user_id}
                        onClick={() => onAssigneeChange(member.user_id)}
                        className="text-slate-700 dark:text-slate-200 focus:text-slate-700 dark:focus:text-slate-200 hover:text-slate-700 dark:hover:text-slate-200 focus:bg-accent/10 hover:bg-accent/10 px-4"
                      >
                        <Avatar className="w-6 h-6 mr-2">
                          <AvatarImage 
                            src={member.profile_picture_url} 
                            alt={member.display_name}
                          />
                          <AvatarFallback className="text-xs">
                            {member.first_name?.charAt(0) || member.username?.charAt(0) || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <span className="flex-1">{member.display_name}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                          ({assigneeTaskCounts.assigneeCounts[member.user_id] || 0})
                        </span>
                      </DropdownMenuItem>
                    ))
                )}
                {assigneeTaskCounts.unassignedCount > 0 && (
                  <DropdownMenuItem 
                    onClick={() => onAssigneeChange('unassigned')}
                    className="text-slate-700 dark:text-slate-200 focus:text-slate-700 dark:focus:text-slate-200 hover:text-slate-700 dark:hover:text-slate-200 focus:bg-accent/10 hover:bg-accent/10 px-4"
                  >
                    <div className="w-6 h-6 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center mr-2">
                      <span className="text-xs text-gray-600 dark:text-gray-300">?</span>
                    </div>
                    <span className="flex-1">Unassigned</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                      ({assigneeTaskCounts.unassignedCount})
                    </span>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
        <div className="space-y-2 min-w-0">
          {/* Removed initial animation to prevent dark flash */}
          <AnimatePresence initial={false} mode="popLayout">
            {taskCount === 0 ? (
              <EmptyActiveTasks 
                onCreateTask={handleCreateTask} 
                isSearching={!!searchQuery}
                searchQuery={searchQuery}
              />
            ) : (
              finalFilteredTasks.map((task) => (
                <div key={task.id} className="min-w-0">
                  <TaskCard 
                    key={task.id} 
                    task={task} 
                    context="project"
                    customTaskOperations={taskOperations}
                    onGenerateContent={onGenerateContent}
                    onGenerateResearch={onGenerateResearch}
                    onGenerateImage={onGenerateImage}
                    isGeneratingContent={isGeneratingContent}
                    workbookId={workbookId}
                  />
                </div>
              ))
            )}
          </AnimatePresence>
        </div>
      </SortableContext>
    </div>
  );
});

GoalActiveTasksSection.displayName = 'GoalActiveTasksSection';

const EmptyActiveTasks = React.forwardRef<HTMLDivElement, { 
  onCreateTask?: () => void; 
  isSearching?: boolean;
  searchQuery?: string;
}>((props, ref) => {
  // Get the navigate function to access the URL parameters
  const navigate = useNavigate();
  const { goalId } = useParams<{ goalId: string }>();
  
  // Function to open the task creation modal
  const handleCreateTask = () => {
    if (props.onCreateTask) {
      props.onCreateTask();
    } else {
      // Fallback to the custom event system
      const event = new CustomEvent('openCreateTaskModal', { detail: { goalId } });
      window.dispatchEvent(event);
    }
  };

  // Show different content based on search state
  if (props.isSearching) {
    return (
      <motion.div 
        ref={ref}
        className="flex flex-col items-center justify-center py-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
      >
        <div className="relative mb-3">
          <div className="relative z-10 bg-gradient-to-br from-gray-400 to-gray-500 dark:from-gray-500 dark:to-gray-600 p-3 rounded-full">
            <Search size={32} className="h-8 w-8 text-white" />
          </div>
        </div>
        
        <h4 className="text-base font-semibold text-gray-600 dark:text-gray-400 mb-2">
          No tasks found
        </h4>
        
        <p className="text-sm text-center px-4 py-2 mb-4 rounded-lg bg-white/50 dark:bg-white/5 backdrop-blur-sm border border-white/20 dark:border-white/10">
          No tasks match "{props.searchQuery}"
        </p>

        <DashboardButton
          onClick={handleCreateTask}
          icon={ClipboardPlus}
          className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600"
        >
          Create New Task
        </DashboardButton>
      </motion.div>
    );
  }
  
  return (
    <motion.div 
      ref={ref}
      className="flex flex-col items-center justify-center py-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
    >
      <div className="relative mb-3">
        <div className="absolute inset-0 bg-gradient-to-r from-amber-300/20 to-yellow-300/20 dark:from-amber-500/10 dark:to-yellow-500/10 rounded-full blur-lg"></div>
        <motion.div
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ 
            repeat: Infinity, 
            repeatType: "reverse", 
            duration: 2,
            ease: "easeInOut"
          }}
        >
          <div className="relative z-10 bg-gradient-to-br from-amber-400 to-yellow-400 dark:from-amber-500 dark:to-yellow-500 p-3 rounded-full">
            <ClipboardPlus size={32} className="h-8 w-8 text-white" />
          </div>
        </motion.div>
      </div>
      
      <h4 className="text-base font-semibold bg-clip-text text-transparent bg-gradient-to-r from-amber-500 to-yellow-500 dark:from-amber-400 dark:to-yellow-400 mb-2">
        Ready to make progress?
      </h4>
      
      <p className="text-sm text-center px-4 py-2 mb-4 rounded-lg bg-white/50 dark:bg-white/5 backdrop-blur-sm border border-white/20 dark:border-white/10">
        Add your first task to start tracking progress toward your goal
      </p>

      <DashboardButton
        onClick={handleCreateTask}
        icon={ClipboardPlus}
        className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600"
      >
        Create New Task
      </DashboardButton>
    </motion.div>
  );
});

EmptyActiveTasks.displayName = 'EmptyActiveTasks';

export default GoalActiveTasksSection; 